import React, {useState, useEffect } from 'react';
import { Modal, Button, Form} from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import './RefundManager.scss';
import { toast } from 'react-toastify';
import { markPaymentReceiptAsRefunded, markPaymentReceiptAsCancelled } from '../../../../../../../../../../crud/payments_api/payment_receipts';
import { getPaymentTransactionsByReceiptId } from '../../../../../../../../../../crud/payments_api/payment_transactions';
import { FaChevronLeft, FaBan } from 'react-icons/fa6';
import CashRefund from './components/CashRefund/CashRefund';
import BankRefund from './components/BankRefund/BankRefund';

interface RefundManagerProps {
  receipt: any;
  show: boolean;
  onClose: () => void;
  onRefundConfirmed?: () => void;
}

const RefundManager: React.FC<RefundManagerProps> = ({
   receipt, 
   show, 
   onClose,
   onRefundConfirmed }) => {

  const [payload, setPayload] = useState<any>({});
  const [refundType, setRefundType] = useState<null | '1' | '2'>(null);  // '1' por defecto (Caja)
  const [isDisabled, setIsDisabled] = useState<boolean>(false)
  const [allowRefund, setAllowRefund] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false); // Para evitar flash al cerrar

  // Payment methods that allow refunds (cash and bank transfers)
  const REFUNDABLE_PAYMENT_TYPES = React.useMemo(() => [
    'efectivo', 'cash', 'efectivo', 'caja',
    'transferencia', 'transfer', 'banco', 'bank transfer'
  ], []);

  // Check if payment methods allow refunds
  useEffect(() => {
    if (!receipt || !show) {
      setAllowRefund(false);
      setIsProcessing(false); // Reset cuando se cierra el modal
      setRefundType(null); // Reset tipo de reembolso
      return;
    }

    setLoading(true);
    setIsProcessing(false); // Reset al abrir
    getPaymentTransactionsByReceiptId(receipt.receipt_id)
      .then((response) => {
        const receiptTransactions = response.data;
        
        // Check if any transaction uses a refundable payment method
        const hasRefundablePayment = receiptTransactions.some((transaction: any) =>
          REFUNDABLE_PAYMENT_TYPES.some(refundableType =>
            transaction.payment_type_name?.toLowerCase().includes(refundableType.toLowerCase())
          )
        );
        
        setAllowRefund(hasRefundablePayment);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching transactions:', error);
        setAllowRefund(false);
        setLoading(false);
      });
  }, [receipt, show, REFUNDABLE_PAYMENT_TYPES]);

  const handleCancelReceipt = async () => {
    try {
      if (!receipt) {
        toast.error("No hay un recibo válido para cancelar.");
        return;
      }
      
      setIsProcessing(true); // Evitar flash durante el cierre
      await markPaymentReceiptAsCancelled(receipt.receipt_id);
      toast.success("Recibo cancelado correctamente.");
      onClose();
      
      if (onRefundConfirmed) {
        onRefundConfirmed();
      } else {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error("Error al cancelar el recibo:", error);
      toast.error("No se pudo cancelar el recibo.");
      setIsProcessing(false);
    }
  };

  const handleRefundConfirmation = async () => {
    try {
      if (!receipt) {
        toast.error("No hay un recibo válido para procesar la devolución.");
        return;
      }
      
      setIsProcessing(true); // Evitar flash durante el cierre
      await markPaymentReceiptAsRefunded(receipt.receipt_id, payload.refundType, payload.workstationId, payload.description);

      toast.success("Recibo marcado como reembolsado correctamente.");
      onClose();
      
      // Si existe la función onRefundConfirmed, la ejecutamos
      if (onRefundConfirmed) {
        onRefundConfirmed();
      } else {
        // Solo recargamos si no hay callback personalizado
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error("Error al procesar la devolución:", error);
      toast.error("No se pudo procesar la devolución.");
      setIsProcessing(false);
    }
  };


  return (      <Modal show={show} onHide={onClose} centered className='receipt-refund-modal'>
        <Modal.Header>
          <div className='form-modal-title-container'>
            <Modal.Title className='form-modal-title'>
              {loading || isProcessing ? 'Procesando...' : allowRefund ? 'Realizar la devolución' : 'Cancelar recibo'}
            </Modal.Title>
          </div>
          <Button variant="none" onClick={onClose}>
            <X size={24} />
          </Button>
        </Modal.Header><Modal.Body>
     
          {refundType !== null && !isProcessing && 
            <Button className='mp-button light' onClick={() => setRefundType(null)} variant="none">
                <FaChevronLeft/> Volver
            </Button>
          }
          
          {loading || isProcessing ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              {isProcessing ? 'Procesando operación...' : 'Verificando métodos de pago...'}
            </div>
          ) : !allowRefund ? (
            // Show only cancel option for non-refundable payment methods
            <div className="cancel-only-section">
              <div className="payment-method-info">
                <p>Este recibo fue pagado con métodos que no permiten devolución.</p>
                <p>Solo puede cancelar el recibo:</p>
              </div>
              <div className="cancel-button-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                <Button 
                  variant="none" 
                  className="cancel-receipt-button" 
                  onClick={handleCancelReceipt}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                >
                  <FaBan /> Cancelar Recibo
                </Button>
              </div>
            </div>
          ) : (
            // Show refund options for cash and bank transfer payments
            <>
              {/* Selección de tipo de devolución usando ButtonGroup */}
              {refundType === null && 
              <Form>
              <Form.Group className="refund-type-selector">

                  <div
                    className={`refund-type-option ${refundType === '1' ? 'active' : undefined}`}
                    onClick={() => setRefundType('1')}
                  >
                    <span> Efectivo</span>
                    <p> Entrega el dinero al cliente desde la caja del local</p>
                  </div>
                  <div
                    className={`refund-type-option ${refundType === '2' ? 'active' : undefined}`}
                    onClick={() => setRefundType('2')}
                  >
                    <span>Transferencia </span>
                    <p> Transfiere el dinero desde una cuenta de banco a la cuenta
                      del cliente
                    </p>
                  </div>

              </Form.Group>
              </Form>
              }
    
              {/* Si se selecciona "Caja", muestra la estación de trabajo */}
              {refundType === '1' && <CashRefund 
                                              setIsDisabled={setIsDisabled}
                                              amount={receipt.total_payed}
                                              setPayload={setPayload}
                                              show={refundType === '1'}/>}
              
              {refundType === '2' && <BankRefund 
                                              setPayload={setPayload}
                                              receipt={receipt}
                                              setIsDisabled={setIsDisabled}
                                              />}
                                              
            </>
          )}

        </Modal.Body>
        <Modal.Footer>
          <div className="form-modal-footer-custom">

          
          <Button className="form-button-cancel" variant="none" onClick={onClose}>Cancelar</Button>
          {allowRefund && (
            <Button variant="none" onClick={handleRefundConfirmation} disabled={isDisabled} className="form-button-create" >Confirmar</Button>
          )}
          </div>
        </Modal.Footer>
        
      </Modal>

  );
};

export default RefundManager;